import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { SubmitHandler, SubmitErrorHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useAuth } from "../../_auth/useAuth";

import {
  ButtonBar,
  DaButton,
  DaTextField,
  DaNumberFormatField,
  DaCheckboxField,
  DaTabPanel,
  Form,
  FormSectionHeader,
  FormTitle,
  MessageBox,
  MsgBoxType,
  IMessagePanelProps,
  MessagePanel,
  ProgressPanel,
  DaAutocompleteField,
  NumberFormatType,
  HTMLComment,
} from "../_shared";

import { AevTab, AevTabs, FormPaper } from "./Company.styles";
import { IPafClientForm, IItemIdRequest, ICustomer, customerApi, ILookupNAICS, ILookupSearchTerm, lookupApi, ILookupState } from "../../_api";
import { createCompanySchema } from "./company.schema";
import { getCompanyTabFields } from "./constant";
import { AuthRolesEnum, companyAEFields } from "../../_appData";
import { throttle } from "lodash";
import { RoutePaths } from "../../App/Routes/routePaths";

/* ------------------------------------------------------------------ */

export const CompanyAEView: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const navigationState = location.state as { mode?: 'view' } | null;
  const isViewMode = navigationState?.mode === 'view';
  const { getAccessToken, toggleNavigation, user, userHasPrivilege } = useAuth();
  const isListAdmin = userHasPrivilege([AuthRolesEnum.ListAdmin]);

  const [selTabIndex, setSelTabIndex] = useState(0);
  const [firstLogin, setFirstLogin] = useState(false);
  const [msgBoxOpen, setMsgBoxOpen] = useState(false);
  const [msgBoxType, setMsgBoxType] = useState<MsgBoxType>("info");
  const [msgBoxMessage, setMsgBoxMessage] = useState("");
  const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({ message: "" });
  const [progressTitle, setProgressTitle] = useState<string>("Loading Company Data...");
  const [showError, setShowError] = useState<boolean>(false);
  const [showProgress, setShowProgress] = useState<boolean>(true);
  const [naicsOptions, setNaicsOptions] = useState<Array<ILookupNAICS>>([]);
  const [stateOptions, setStateOptions] = useState<Array<ILookupState>>([]);
  const [existingCustomerSid, setExistingCustomerSid] = useState<string | undefined>(undefined);
  const [existingIsInternal, setExistingIsInternal] = useState<boolean | undefined>(undefined);
  const [existingCompanyId, setExistingCompanyId] = useState<number | undefined>(undefined);
  const isListAdminRef = useRef(isListAdmin);
  isListAdminRef.current = isListAdmin;
  const resolver = useMemo(
    () => (values: any, context: any, options: any) =>
      yupResolver(createCompanySchema(isListAdminRef.current), { abortEarly: false })(values, context, options),
    []
  );

  const isRegisterPath =
    params.id === "register" || location.pathname === RoutePaths.COMPANY_REGISTER;

  let customerId: number | undefined = undefined;
  let aevMode: "add" | "upd" | "err" | undefined = undefined;

  // Validate URL param (or register path when route is /company/register)
  if (isRegisterPath) {
    customerId = undefined;
    aevMode = "add";
  } else if (params.id) {
    if (params.id === "new") {
      customerId = undefined;
      aevMode = "add";
    } else if (!isNaN(Number(params.id))) {
      customerId = Number(params.id);
      aevMode = "upd";
    } else {
      aevMode = "err";
    }
  }

  const isEditMode = aevMode === "upd";
  useEffect(() => {
    setFirstLogin(isRegisterPath);
  }, [isRegisterPath]);


  const formFieldSize = "medium";
  const formFieldVariant = "standard";

  const form = useForm<IPafClientForm>({
    resolver,
    mode: "onChange",
    defaultValues: {
      company__company_name: "",
      company__parent_company_name: "",
      company__dba_name: "",
      company__naics_code: "",
      company__federal_tax_id: "",
      company__usps_mailer_id: "",
      company__carrier_route_id: "",
      company__company_registration_id: "",
      company__company_website: "",
      company__customer_since_date: "",
      company__address1: "",
      company__address2: "",
      company__city: "",
      company__state: "",
      company__postal_code: "",
      company__urbanization: "",
      company__representative_name: "",
      company__representative_title: "",
      company__representative_email: "",
      company__phone: "",
      is_foreign_location: false,
      is_ncoa: false,
      is_dsf: false,
      is_triplex: false,
      is_waiver_on_file: false,
      company__is_internal: false,
      listadmin_type: 1,
      notes: "",
    },
  });

  const { control, handleSubmit, trigger, reset, setValue, formState: { errors } } = form;

  const handleNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent any implicit form submission when moving between tabs
    e?.preventDefault();
    if (isViewMode) {
      // In view mode, just move to next tab without validation
      setSelTabIndex((p) => p + 1);
    } else {
      const tabFields = getCompanyTabFields(isListAdmin)[selTabIndex];
      const valid = await trigger(tabFields);
      if (valid) setSelTabIndex((p) => p + 1);
    }
  };
  const handlePrevious = (e?: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent any implicit form submission when moving between tabs
    e?.preventDefault();
    setSelTabIndex((p) => Math.max(0, p - 1));
  };

  const onSubmit: SubmitHandler<IPafClientForm> = async (data) => {
    setProgressTitle(aevMode === "add" ? "Creating Company..." : "Updating Company...");
    setShowProgress(true);

    try {
      // Map form data to ICustomer interface
      // For update, API expects company_id; for add, customer_id is used
      const customerData: any = {
        // For update, use company_id from API response; for add, use customer_id
        ...(aevMode === "upd" && existingCompanyId
          ? { company_id: existingCompanyId }
          : { customer_id: customerId }),
        company_sid: existingCustomerSid || "",
        company_name: data.company__company_name,
        parent_company_name: data.company__parent_company_name || "",
        dba_name: data.company__dba_name || "",
        address1: data.company__address1,
        address2: data.company__address2 || "",
        city: data.company__city,
        state: data.company__state,
        postal_code: data.company__postal_code,
        urbanization: data.company__urbanization || "",
        phone: data.company__phone,
        federal_tax_id: data.company__federal_tax_id?.trim() || "",
        naics_code: data.company__naics_code || "",
        usps_mailer_id: data.company__usps_mailer_id?.trim() || "",
        company_registration_id: data.company__company_registration_id?.trim() || "",
        carrier_route_id: data.company__carrier_route_id?.trim() || "",
        company_website: data.company__company_website || "",
        representative_name: data.company__representative_name,
        representative_title: data.company__representative_title || "",
        representative_email: data.company__representative_email,
        is_internal: data.company__is_internal ?? existingIsInternal ?? false,
        is_foreign_location: data.is_foreign_location,
        is_ncoa: data.is_ncoa,
        is_dsf: data.is_dsf,
        is_triplex: data.is_triplex,
        is_waiver_on_file: data.is_waiver_on_file,
        customer_since_date: undefined,
        create_datetime: undefined,
        modify_datetime: undefined,
        modify_user_name: undefined,
        company_type: isListAdmin ? "1" : "0"
      };

      const auth_token = await getAccessToken();
      let resp;

      if (aevMode === "add") {
        resp = await customerApi.add(customerData, auth_token);
      } else if (aevMode === "upd") {
        resp = await customerApi.update(customerData, auth_token);
      }

      if (resp) {
        if (resp.success) {
          setMsgBoxType("info");
          setMsgBoxMessage(`Company ${aevMode === "add" ? "created" : "updated"} successfully.`);
          setMsgBoxOpen(true);
          // Navigate after a short delay to show success message
          setTimeout(() => {
            if (firstLogin) {
              toggleNavigation();
              navigate(RoutePaths.PAFS, { replace: true });
            } else {
              navigate(RoutePaths.COMPANIES, { replace: true });
            }
          }, 1500);
        } else if (resp.error && resp.error.length > 0) {
          showErrorPanel({
            title: `Company ${aevMode === "add" ? "Creation" : "Update"} Error`,
            message: resp.error.join("\n"),
          });
        }
      }
    } catch (error: any) {
      showErrorPanel({
        title: `Company ${aevMode === "add" ? "Creation" : "Update"} Error`,
        message: error.message || "An error occurred while saving the company.",
      });
    } finally {
      setShowProgress(false);
    }
  };

  const onSubmitErrors: SubmitErrorHandler<IPafClientForm> = (errors) => {
    const msgs: string[] = [];
    const walk = (o: any) =>
      Object.values(o).forEach((v: any) => {
        if (v?.message) msgs.push(v.message);
        if (typeof v === "object") walk(v);
      });
    walk(errors);

    setMsgBoxType("warning");
    setMsgBoxMessage(`Please fix:\n\n${msgs.map(m => `• ${m}`).join("\n")}`);
    setMsgBoxOpen(true);
  };

  const showErrorPanel = ({ title, message, type = "error" }: IMessagePanelProps) => {
    setMsgBoxType("error");
    setMsgBoxMessage(`${title ? title + "\n\n" : ""}${message}`);
    setMsgBoxOpen(true);
  }

  const getCompanyData = useCallback(async (): Promise<void> => {
    setProgressTitle("Loading Company Data...");
    setShowProgress(true);

    if (aevMode === "err") {
      showErrorPanel({
        title: "Invalid Company ID",
        message: "An error occurred while retrieving Company data. " +
          "Please go to the Company list view and retry your latest action."
      });
      setShowProgress(false);
    } else if (aevMode === "add") {
      // New Company - Auto-fill contact info for registration
      if (isRegisterPath && user) {
        setValue("company__representative_name", `${user.first_name || ''} ${user.last_name || ''}`.trim());
        setValue("company__representative_email", user.email || '');
        setValue("company__phone", user.phone_number || '');
      }
      setShowProgress(false);
    } else if (aevMode === "upd" && customerId) {
      const options: IItemIdRequest = { id: customerId };
      const auth_token = await getAccessToken();

      customerApi.get_by_id(options, auth_token)
        .then(async (resp) => {
          if (resp) {
            if (resp.success) {
              // Map customer data to form fields
              const customerData = resp.data as ICustomer;

              // Pre-populate NAICS options if naics_code exists
              if (customerData.naics_code) {
                const naicsOptions = await getNaicsOptions(customerData.naics_code);
                if (naicsOptions) {
                  setNaicsOptions(naicsOptions as Array<ILookupNAICS>);
                }
              }

              // Pre-populate State options if state exists
              if (customerData.state) {
                const stateOptions = await getStateOptions(customerData.state);
                if (stateOptions) {
                  setStateOptions(stateOptions as Array<ILookupState>);
                }
              }

              const formData: IPafClientForm = {
                company__company_name: customerData.company_name || "",
                company__parent_company_name: customerData.parent_company_name || "",
                company__dba_name: customerData.dba_name || "",
                company__naics_code: customerData.naics_code || "",
                company__federal_tax_id: customerData.federal_tax_id || "",
                company__usps_mailer_id: customerData.usps_mailer_id || "",
                company__carrier_route_id: customerData.carrier_route_id || "",
                company__company_registration_id: (customerData as any).company_registration_id || "",
                company__company_website: customerData.company_website || "",
                company__customer_since_date: customerData.customer_since_date || "",
                company__address1: customerData.address1 || "",
                company__address2: customerData.address2 || "",
                company__city: customerData.city || "",
                company__state: customerData.state || "",
                company__postal_code: customerData.postal_code || "",
                company__urbanization: customerData.urbanization || "",
                company__representative_name: customerData.representative_name || "",
                company__representative_title: customerData.representative_title || "",
                company__representative_email: customerData.representative_email || "",
                company__phone: customerData.phone || "",
                is_foreign_location: (customerData as any).is_foreign_location ?? false,
                is_ncoa: (customerData as any).is_ncoa ?? false,
                is_dsf: (customerData as any).is_dsf ?? false,
                is_triplex: (customerData as any).is_triplex ?? false,
                is_waiver_on_file: (customerData as any).is_waiver_on_file ?? false,
                company__is_internal: (customerData as any).is_internal ?? false,
              };
              reset(formData);

              // Preserve values needed for update payload
              setExistingCustomerSid(customerData.company_sid);
              setExistingIsInternal((customerData as any).is_internal);
              // Store company_id from API response (response has company_id, not customer_id)
              setExistingCompanyId((resp.data as any).company_id);
            } else if (resp.error && resp.error.length > 0) {
              showErrorPanel({
                title: "Company Not Found Error",
                message: "An error occurred while retrieving Company data. " +
                  "The Company data was not found.",
              });
            }
          }
        })
        .catch(() => {
          showErrorPanel({
            title: "Company API Fetch Error",
            message: "An error occurred while fetching Company data. " +
              "There may have been a network error.",
          });
        })
        .finally(() => {
          setShowProgress(false);
        });
    }
  }, [reset, customerId, aevMode, isRegisterPath, user, setValue]);
  // Lookup Field methods
  const stateSearch = React.useRef(
    throttle(async (search_term) => {
      if (search_term.length < 2) {
        setStateOptions([]);
      } else {
        setStateOptions(await getStateOptions(search_term) as Array<ILookupState>);
      }
    }, 250)
  ).current;

  const stateInputChanged = async (e: React.SyntheticEvent, newValue: string, reason: string): Promise<void> => {
    // Don't re-run search for
    // programmatic change, user selection
    if (reason !== "reset") {
      stateSearch(newValue);
    }
  }

  const getStateOptions = async (search_term: string): Promise<Array<ILookupState> | void> => {
    const options: ILookupSearchTerm = { search_term: search_term };
    const auth_token = await getAccessToken();
    try {
      const resp = await lookupApi.get_state_lookup(options, auth_token);
      if (resp) {
        if (resp.success) {
          return resp.data;
        } else if (resp.error && resp.error.length > 0) {
          showErrorPanel({
            title: "State lookups Not Found Error",
            message: "An error occurred while retrieving State lookups data. " +
              "The State lookups data was not found.",
          });
        }
      }
    }
    catch (err) {
      showErrorPanel({
        title: "Lookup API Fetch Error",
        message: "An error occurred while fetching State lookups data. " +
          "There may have been a network error.",
      });
    }
  };
  const naicsSearch = React.useRef(
    throttle(async (search_term) => {
      if (search_term.length < 3) {
        setNaicsOptions([]);
      } else {
        setNaicsOptions(await getNaicsOptions(search_term) as Array<ILookupNAICS>);
      }
    }, 250)
  ).current;

  const naicsInputChanged = async (e: React.SyntheticEvent, newValue: string, reason: string): Promise<void> => {
    // Don't re-run search for
    // programmatic change, user selection
    if (reason !== "reset") {
      naicsSearch(newValue);
    }
  }

  const getNaicsOptions = async (search_term: string): Promise<Array<ILookupNAICS> | void> => {
    const options: ILookupSearchTerm = { search_term: search_term };
    const auth_token = await getAccessToken();
    try {
      const resp = await lookupApi.get_naics_lookup(options, auth_token);
      if (resp) {
        if (resp.success) {
          return resp.data;
        } else if (resp.error && resp.error.length > 0) {
          showErrorPanel({
            title: "NAICS lookups Not Found Error",
            message: "An error occurred while retrieving NAICS lookups data. " +
              "The NAICS lookups data was not found.",
          });
        }
      }
    }
    catch (err) {
      showErrorPanel({
        title: "Lookup API Fetch Error",
        message: "An error occurred while fetching NAICS lookups data. " +
          "There may have been a network error.",
      });
    }
  };
  const getCompanyTitle = () => {
    if (aevMode === "add") return "Company : New";
    if (isViewMode) return "Company : View";
    return "Company : Update";
  }

  useEffect(() => {
    // Get data from remote service
    getCompanyData();
  }, [getCompanyData]);

  return (
    <>
      <FormPaper elevation={4}>
        <FormTitle
          title={getCompanyTitle()}
        />

        <Grid container>
          {showProgress ? (
            <Grid container justifyContent={"center"} item>
              <ProgressPanel title={progressTitle} />
            </Grid>
          ) : (showError ? (
            <Grid container justifyContent={"center"} item>
              <MessagePanel
                {...msgPanelProps}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Form onSubmit={handleSubmit(onSubmit, onSubmitErrors)}>
                <AevTabs value={selTabIndex} onChange={(_, i) => setSelTabIndex(i)}>
                  <AevTab label="Company" />
                  <AevTab label="Address" />
                  <AevTab label="Contact" />
                </AevTabs>

                <Box sx={{ minHeight: theme.spacing(73) }}>
                  {/* COMPANY */}
                  <DaTabPanel selectedIndex={selTabIndex} index={0}>
                    <FormSectionHeader title="Company Info" />
                    <Grid container spacing={theme.spacing(1)}>
                      <Grid item xs={12} md={12}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.companyName.name}
                          label={companyAEFields.companyName.label}
                          required={companyAEFields.companyName.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      {!isListAdmin && <><Grid item xs={12} md={12}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.parentCompanyName.name}
                          label={companyAEFields.parentCompanyName.label}
                          required={companyAEFields.parentCompanyName.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                        <Grid item xs={12} md={12}>
                          <DaTextField
                            control={control}
                            name={companyAEFields.dbaName.name}
                            label={companyAEFields.dbaName.label}
                            required={companyAEFields.dbaName.required}
                            variant={formFieldVariant}
                            size={formFieldSize}
                            errors={errors}
                            readOnly={isViewMode}
                          />
                        </Grid>
                      </>}
                      <Grid item xs={12} md={ isListAdmin ? 6 : 4}>
                        <DaAutocompleteField
                          control={control}
                          name={companyAEFields.naicsCode.name}
                          label={companyAEFields.naicsCode.label}
                          required={companyAEFields.naicsCode.required}
                          options={naicsOptions.map(({ naics_code, naics_title }) =>
                            ({ value: naics_code, label: `${naics_code} - ${naics_title}` })
                          )}
                          onInputChange={naicsInputChanged}
                          noOptionsText="Type your industry category to find the NAICS"
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      {!isListAdmin && <><Grid item xs={12} md={4}>
                        <DaNumberFormatField
                          control={control}
                          name={companyAEFields.uspsMailerId.name}
                          label={companyAEFields.uspsMailerId.label}
                          required={companyAEFields.uspsMailerId.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          numberFormat={NumberFormatType.MAILER_ID}
                          readOnly={isViewMode}
                        />
                      </Grid>
                        <Grid item xs={12} md={4}>
                          <DaNumberFormatField
                            control={control}
                            name={companyAEFields.customerRegistrationId.name}
                            label={companyAEFields.customerRegistrationId.label}
                            required={companyAEFields.customerRegistrationId.required}
                            variant={formFieldVariant}
                            size={formFieldSize}
                            errors={errors}
                            numberFormat={NumberFormatType.CR_ID}
                            readOnly={isViewMode}
                          />
                        </Grid>
                      </>}
                      {isListAdmin && <Grid item xs={12} md={6}>
                        <DaNumberFormatField
                          control={control}
                          name={companyAEFields.federalTaxId.name}
                          label={companyAEFields.federalTaxId.label}
                          required={companyAEFields.federalTaxId.required}
                          // readOnly={isListAdminTabReadOnly || isPartialEditMode}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          numberFormat={NumberFormatType.EIN}
                        />
                      </Grid>
                      }
                      <Grid item xs={12} md={4}>
                        <DaCheckboxField
                          control={control}
                          name={companyAEFields.isForeignLocation.name}
                          label={companyAEFields.isForeignLocation.label}
                          size={formFieldSize}
                          errors={errors}
                          disabled={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DaCheckboxField
                          control={control}
                          name={companyAEFields.isNcoa.name}
                          label={<>NCOA<sup>Link</sup> Service</>}
                          size={formFieldSize}
                          errors={errors}
                          disabled={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DaCheckboxField
                          control={control}
                          name={companyAEFields.isDsf.name}
                          label={<>DSF<sup>2</sup> Service</>}
                          size={formFieldSize}
                          errors={errors}
                          disabled={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DaCheckboxField
                          control={control}
                          name={companyAEFields.isTriplex.name}
                          label={companyAEFields.isTriplex.label}
                          size={formFieldSize}
                          errors={errors}
                          disabled={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DaCheckboxField
                          control={control}
                          name={companyAEFields.isWaiverOnFile.name}
                          label={<>Waive NCOA<sup>Link</sup> 7-Day Fulfillment</>}
                          size={formFieldSize}
                          errors={errors}
                          disabled={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DaCheckboxField
                          control={control}
                          name={companyAEFields.customerIsInternal.name}
                          label={companyAEFields.customerIsInternal.label}
                          size={formFieldSize}
                          errors={errors}
                          disabled={isViewMode}
                        />
                      </Grid>
                    </Grid>
                  </DaTabPanel>

                  {/* Address Tab */}
                  <DaTabPanel selectedIndex={selTabIndex} index={1}>
                    <Grid container spacing={theme.spacing(1)}>
                      <Grid item xs={12} md={12}>
                        <FormSectionHeader
                          title={"Address"}
                          typeVariant={"h5"}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.address1.name}
                          label={companyAEFields.address1.label}
                          required={companyAEFields.address1.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.address2.name}
                          label={companyAEFields.address2.label}
                          required={companyAEFields.address2.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.city.name}
                          label={companyAEFields.city.label}
                          required={companyAEFields.city.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DaAutocompleteField
                          control={control}
                          name={companyAEFields.state.name}
                          label={companyAEFields.state.label}
                          required={companyAEFields.state.required}
                          options={stateOptions.map(
                            ({ state_code, state_name, country_code }) =>
                              ({ value: state_code, label: `${state_code} - ${state_name} (${country_code})` })
                          )}
                          onInputChange={stateInputChanged}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.postalCode.name}
                          label={companyAEFields.postalCode.label}
                          required={companyAEFields.postalCode.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.urbanization.name}
                          label={companyAEFields.urbanization.label}
                          required={companyAEFields.urbanization.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <HTMLComment text={"Placeholder"} />
                      </Grid>
                    </Grid>
                  </DaTabPanel>
                  {/* Contact Tab */}
                  <DaTabPanel selectedIndex={selTabIndex} index={2}>
                    <Grid container spacing={theme.spacing(1)}>
                      <Grid item xs={12} md={12}>
                        <FormSectionHeader
                          title={"Contact Info"}
                          typeVariant={"h5"}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.representativeName.name}
                          label={companyAEFields.representativeName.label}
                          required={companyAEFields.representativeName.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.representativeTitle.name}
                          label={companyAEFields.representativeTitle.label}
                          required={companyAEFields.representativeTitle.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.representativeEmail.name}
                          label={companyAEFields.representativeEmail.label}
                          required={companyAEFields.representativeEmail.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode || isEditMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <DaTextField
                          control={control}
                          name={companyAEFields.companyWebsite.name}
                          label={companyAEFields.companyWebsite.label}
                          required={companyAEFields.companyWebsite.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          readOnly={isViewMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DaNumberFormatField
                          control={control}
                          name={companyAEFields.phone.name}
                          label={companyAEFields.phone.label}
                          required={companyAEFields.phone.required}
                          variant={formFieldVariant}
                          size={formFieldSize}
                          errors={errors}
                          numberFormat={NumberFormatType.US_Phone}
                          readOnly={isViewMode}
                        />
                      </Grid>
                    </Grid>
                  </DaTabPanel>
                </Box>

                <ButtonBar buttonAlignment="alignRight">
                  {isViewMode ? (
                    // View mode: Show Previous and Next on middle tabs, Previous and Cancel on last tab
                    <>
                      {selTabIndex === 0 ? (
                        // First tab: Show Next only
                        <DaButton buttonStyle="primary" type="button" onClick={(e) => handleNext(e)}>
                          Next
                        </DaButton>
                      ) : selTabIndex === 1 ? (
                        // Middle tab: Show Previous and Next
                        <>
                          <DaButton buttonStyle="secondary" type="button" onClick={(e) => handlePrevious(e)}>
                            Previous
                          </DaButton>
                          <DaButton buttonStyle="primary" type="button" onClick={(e) => handleNext(e)}>
                            Next
                          </DaButton>
                        </>
                      ) : (
                        // Last tab: Show Previous and Cancel
                        <>
                          <DaButton buttonStyle="secondary" type="button" onClick={(e) => handlePrevious(e)}>
                            Previous
                          </DaButton>
                          <DaButton buttonStyle="secondary"
                            onClick={() => navigate("/companies")}
                            disabled={showProgress || firstLogin}>
                            Cancel
                          </DaButton>
                        </>
                      )}
                    </>
                  ) : (
                    // Edit/Add mode: Original behavior
                    <>
                      <DaButton buttonStyle="secondary"
                        onClick={() => navigate("/companies")}
                        disabled={showProgress || firstLogin}>
                        Cancel
                      </DaButton>
                      {selTabIndex < 2 ? (
                        <DaButton buttonStyle="primary" type="button" onClick={(e) => handleNext(e)}>
                          Next
                        </DaButton>
                      ) : (
                        <DaButton buttonStyle="primary" type="submit">
                          Save
                        </DaButton>
                      )}
                    </>
                  )}
                </ButtonBar>
              </Form>
            </Grid>
          ))}
        </Grid>
      </FormPaper>

      <MessageBox
        isOpen={msgBoxOpen}
        onClose={() => setMsgBoxOpen(false)}
        message={msgBoxMessage}
        type={msgBoxType}
      />
    </>
  );
};
